import { useEffect, useRef } from "react";

import { ErrorText } from "~/ui/common/ErrorText";
import { tw } from "~/utils";

interface OtpInputProps {
  otp: string[];
  onChange: (otp: string[]) => void;
  error?: string;
}

export const OtpInput = ({ otp, onChange, error }: OtpInputProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const focusAt = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const setDigit = (index: number, value: string) => {
    const newOtp = [...otp];
    newOtp[index] = value;
    onChange(newOtp);
  };

  const handleChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const digit = e.target.value.replace(/\D/g, "").slice(-1);
    setDigit(index, digit);
    if (digit && index < otp.length - 1) focusAt(index + 1);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (otp[index]) setDigit(index, "");
      else if (index > 0) focusAt(index - 1);
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusAt(index - 1);
    } else if (e.key === "ArrowRight" && index < otp.length - 1) {
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, otp.length);
    if (!pasted) return;
    const newOtp = Array(otp.length).fill("");
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    onChange(newOtp);
    focusAt(Math.min(pasted.length, otp.length - 1));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            value={digit}
            placeholder="-"
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            aria-label={`Digit ${index + 1} of ${otp.length}`}
            className={tw(
              "h-14 min-w-0 flex-1 rounded-md border text-center text-lg font-semibold text-blue-900",
              "placeholder:text-input-placeholder placeholder:font-normal",
              "focus:border-violet focus:ring-violet focus:outline-none focus:ring-1",
              digit ? "border-blue-900" : "border-blueGrey",
            )}
          />
        ))}
      </div>
      {error && <ErrorText id="otp-error">{error}</ErrorText>}
    </div>
  );
};
